import { useState } from 'react';
import classNames from 'classnames';
import { Button, Tag } from 'antd';
import { useDispatch } from 'react-redux';
import { PlusOutlined } from '@ant-design/icons';
import { FiChevronRight } from '@react-icons/all-files/fi/FiChevronRight';
import BuyBoxMatchedPropertyCount from '../BuyBoxMatchedPropertyCount';

import './index.scss';
import WithAnimation from '../../../../components/WithAnimation';
import { setMatchingCountShown } from '../../../../reducers/Buyer/actions';
import { setModalState } from '../../../../reducers/Modal/actions';
import { MODAL_UNSAVED_BUYBOX_CHANGES } from '../../../../reducers/Modal/types';
import { UnsavedBuyBoxChangesModal } from '../UnsavedBuyBoxChangesModal';
import { reportEvent } from '../../../../services/analyticsService';

const getGooglePlaceFriendlyName = (place) => {
  if (!place?.kind) return '';
  switch (place.kind) {
    case 'locality':
      return place.locality;
    case 'state':
      return place.state;
    case 'county':
      return `${place.county} County`;
    case 'zipcode':
      return place.zipcode;
    default:
      return '';
  }
};
const getAreaSnippet = (areas) => {
  if (!areas || areas.length === 0) return '';
  const titles = areas.map((area) => getGooglePlaceFriendlyName(area));

  let res = titles[0];
  if (titles.length > 1) {
    res += `, ${titles[1]}`;
  }
  if (titles.length > 2) {
    res += `, and ${titles.length - 2} more`;
  }

  return res;
};

export default (props) => {
  const {
    buyBoxList = [],
    selectedBuyBox,
    setSelectedBuyBox,
    buyBoxPropertyMatch,
    isFetchingBuyboxMatchedProperties,
    isMatchingCountShown,
    hasUnsavedChanges,
  } = props;
  const dispatch = useDispatch();
  const [clickedBuyBox, setClickedBuyBox] = useState();

  const clickHandler = (buyBox) => {
    if (hasUnsavedChanges) {
      dispatch(setModalState(MODAL_UNSAVED_BUYBOX_CHANGES, true));
      setClickedBuyBox(buyBox);
    } else {
      setSelectedBuyBox(buyBox);
      // Hide matching count if BuyBox is changed
      if (buyBox._id !== selectedBuyBox._id) {
        dispatch(setMatchingCountShown(false));
      }
    }
  };

  const addBuyboxHandler = () => {
    reportEvent('buybox', 'add');
    if (hasUnsavedChanges) {
      dispatch(setModalState(MODAL_UNSAVED_BUYBOX_CHANGES, true));
    } else {
      props.onCreateCriteria();
    }
  };
  const handleDiscardChanges = () => {
    dispatch(setModalState(MODAL_UNSAVED_BUYBOX_CHANGES, false));
    if (clickedBuyBox) {
      setSelectedBuyBox(clickedBuyBox);
    } else {
      props.onCreateCriteria();
    }
  };

  return (
    <div className="buybox-list">
      <div className="actions">
        <Button type="link" icon={<PlusOutlined />} onClick={addBuyboxHandler}>
          {buyBoxList.length > 0 ? 'Add another' : 'Add Criteria'}
        </Button>
      </div>

      {/* Mobile View */}
      <div className="buybox-list-mobile">
        {buyBoxList.map((buyBoxItem) => (
          <Tag
            className={classNames('buybox-tag', {
              selected: buyBoxItem._id === selectedBuyBox?._id,
            })}
            key={buyBoxItem._id}
            onClick={() => clickHandler(buyBoxItem)}
          >
            <span className="tag-title">{buyBoxItem.title || 'Unnamed'}</span>
            {buyBoxItem._id === selectedBuyBox?._id && isMatchingCountShown && (
              <BuyBoxMatchedPropertyCount
                matchingCount={buyBoxPropertyMatch?.[buyBoxItem._id]}
                isLoading={isFetchingBuyboxMatchedProperties}
              />
            )}
          </Tag>
        ))}
      </div>

      {/* Desktop View */}
      <div className="buybox-list-desktop">
        {buyBoxList.map((buyBoxItem) => (
          <div
            className={classNames('buybox-list-item', {
              selected: buyBoxItem._id === selectedBuyBox?._id,
            })}
            key={buyBoxItem._id}
            onClick={() => clickHandler(buyBoxItem)}
          >
            <div className="left">
              <div className="title">{buyBoxItem.title || 'Unnamed'}</div>
              <div className="subtitle">
                {buyBoxItem._id
                  ? getAreaSnippet(buyBoxItem.areas)
                  : 'Unsaved changes'}
              </div>
              <WithAnimation
                isRender={
                  buyBoxItem._id === selectedBuyBox?._id && isMatchingCountShown
                }
              >
                <BuyBoxMatchedPropertyCount
                  matchingCount={buyBoxPropertyMatch?.[buyBoxItem._id]}
                  isLoading={isFetchingBuyboxMatchedProperties}
                />
              </WithAnimation>
            </div>
            <div className="right">
              <FiChevronRight />
            </div>
          </div>
        ))}
      </div>
      <UnsavedBuyBoxChangesModal handleDiscardChanges={handleDiscardChanges} />
    </div>
  );
};
